import { Box, Collapse, List, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed.tsx";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader.tsx";
import useFetch from "@entrilia/shared/hooks/useFetch.ts";
import { logError } from "@entrilia/shared/logging.ts";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid.ts";
import biApi from "../../../../../../../../api/biApi.ts";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch.ts";
import { selectAccountBookSettings } from "../../../../store/selectors/clientSettingsSelectors.ts";
import { selectConditions } from "../../../../store/selectors/fieldsStateSelectors.ts";
import {
  isPostingAccount,
  selectChartOfAccountsState,
  selectMeasures,
} from "../../../../store/selectors/metaDataSelectors.ts";
import { createMetaDataAction, createUiSettingsAction } from "../../../../store/utils/storeUtils.ts";
import type { FieldsSectionControl } from "../../common/fields/fieldsExpansionState.ts";
import FieldsSectionHeader from "../../common/fields/FieldsSectionHeader.tsx";
import type { StatementChartLedger } from "../utils/statementChartOfAccounts.ts";
import {
  getStatementAccounts,
  groupStatementAccountsByCategory,
  searchStatementAccounts,
} from "../utils/statementChartOfAccounts.ts";
import { findMemoAmountMeasure } from "../utils/statementRowsArea.ts";
import ChartOfAccountsCategory from "./ChartOfAccountsCategory.tsx";

interface Props {
  searchText: string;
  disabled: boolean;
  section: FieldsSectionControl;
}

const ChartOfAccountsSection = ({ searchText, disabled, section }: Props) => {
  const dispatch = useAppDispatch();
  const chartOfAccountsState = useSelector(selectChartOfAccountsState);
  const accountingBookSettings = useSelector(selectAccountBookSettings);
  const conditions = useSelector(selectConditions);
  const measures = useSelector(selectMeasures);
  const [ledger, setLedger] = useState<StatementChartLedger>("All");

  const fetchChartOfAccounts = useCallback(() => biApi.getChartOfAccounts(), []);
  const [, fetchError] = useFetch(fetchChartOfAccounts, {
    disabled: chartOfAccountsState !== undefined,
    onSetData: (data) => dispatch(createMetaDataAction("setChartOfAccounts", data)),
  });

  useEffect(() => {
    if (fetchError) {
      logError(fetchError, "[ChartOfAccountsSection] getChartOfAccounts");
    }
  }, [fetchError]);

  const handleDragStart = useCallback(() => {
    dispatch(createUiSettingsAction("setDragSessionId", generateGuid()));
  }, [dispatch]);

  const memoAvailable = useMemo(
    () => findMemoAmountMeasure(measures) !== undefined && (chartOfAccountsState?.memo ?? []).some(isPostingAccount),
    [measures, chartOfAccountsState]
  );
  const accounts = useMemo(
    () =>
      chartOfAccountsState
        ? getStatementAccounts(
            chartOfAccountsState,
            ledger,
            accountingBookSettings?.multiBook === true,
            conditions,
            memoAvailable
          )
        : [],
    [chartOfAccountsState, ledger, accountingBookSettings, conditions, memoAvailable]
  );
  const groups = useMemo(
    () => groupStatementAccountsByCategory(searchStatementAccounts(accounts, searchText)),
    [accounts, searchText]
  );

  const handleLedgerChange = (_: unknown, value: StatementChartLedger | null) => {
    if (value) {
      setLedger(value);
    }
  };

  if (chartOfAccountsState === undefined) {
    if (fetchError) {
      return <DataLoadingFailed title="Unable to load the chart of accounts" />;
    }
    return <InlineLoader />;
  }

  const isSearching = searchText !== "";
  const emptyText = isSearching
    ? "No accounts match the search."
    : ledger === "Memo"
      ? "This chart of accounts has no Memo accounts."
      : "No accounts.";

  return (
    <Stack>
      <FieldsSectionHeader title="Chart of Accounts" open={section.open} onToggle={section.onToggle} />
      <Collapse orientation="vertical" in={section.open} sx={{ "& .MuiCollapse-wrapperInner": { display: "block" } }}>
        {memoAvailable && (
          <Box sx={{ pl: 2, pr: 0.75, pb: 0.75 }}>
            <ToggleButtonGroup
              exclusive
              fullWidth
              value={ledger}
              onChange={handleLedgerChange}
              sx={{ "& .MuiToggleButton-root": { height: 30, py: 0 } }}
            >
              <ToggleButton value="All">All</ToggleButton>
              <ToggleButton value="Gl">G/L</ToggleButton>
              <ToggleButton value="Memo">Memo</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}
        {groups.length === 0 ? (
          <Typography variant="caption" color="textSecondary" sx={{ display: "block", px: 0.75, pt: 0.5, pb: 1 }}>
            {emptyText}
          </Typography>
        ) : (
          <List key={section.groupsEpoch} sx={{ p: 0, pl: 2 }}>
            {groups.map((group) => (
              <ChartOfAccountsCategory
                key={group.category}
                group={group}
                defaultExpanded={isSearching || section.groupsOpen}
                disabled={disabled}
                onDragStart={handleDragStart}
              />
            ))}
          </List>
        )}
      </Collapse>
    </Stack>
  );
};

export default ChartOfAccountsSection;
